import * as React from 'react';
import { CardHeader } from '../common/authenticationCardComponents/CardHeader';
import { PasswordComplexityIndicator } from '../common/requirementManagers/PasswordComplexityIndicator';
import { PasswordValidator } from '../../services/validators/PasswordValidator';
import { ReactNode, useEffect, useState } from 'react';
import { Nullable } from '../../utils/types';
import { PageRoutes } from '../root/Routes';
import { authenticationService, User } from '../../services/authentication';
import { LogManager } from '../../utils/LogManager';
import { LockIcon } from '../common/Icons';
import { History } from 'history';
import { parseQueryParameters } from '../../utils/utilFunctions';
import { LucidLinkLogo } from '../common/Icons';
import { I_ALREADY_HAVE_AN_ACCOUNT_MESSAGE, LUCIDLINK_WEBSITE, SIGN_IN_MESSAGE } from '../../utils/constants';
import { AuthenticationFooter } from '../common/authenticationCardComponents/AuthenticationFooter';
import { MARKETING_DATA_KEYS, MarketingData, MarketingDataKey } from '../../interfaces/MarketingDataInterface';
import { AuthenticationFooterSecondary } from '../common/authenticationCardComponents/AuthenticationFooterSecondary';

interface PasswordStepProps {
    history: History;
    email: string;
    queryParams: any;
}

const logManager = new LogManager('Register');

export default function PasswordStep(props: PasswordStepProps): JSX.Element {
    const [password, setPassword] = useState('');
    const [isPasswordValid, setIsPasswordValid] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (!props.email) {
            props.history.push(PageRoutes.Register);
            return;
        }
    });

    return (
        <div className="auth_wrapper">
            <header>
                <a href={LUCIDLINK_WEBSITE}>
                    <LucidLinkLogo width={'130px'} height={'50px'}/>
                </a>
            </header>
            <div className="auth_block">
                <div>
                    <CardHeader
                        message={headerMessage()}
                        email={props.email}
                        authStep={'password'}
                        history={props.history}
                        step={PageRoutes.Register}
                    />
                    <div className="auth_form">
                        <div className="float-label-control">
                            <input
                                type="password"
                                placeholder="Choose password"
                                className="form-control"
                                name="password"
                                id="password"
                                autoFocus={true}
                                onChange={handlePasswordChange}
                                onKeyDown={async (e) => {await handleEnter(e, props); }}
                            />
                            <label>Choose password</label>
                            <div className="row">
                                <span className="minitext pt-2 red">{errorMessage}</span>
                            </div>
                            <span className="icon">
                                <LockIcon
                                    width={'17px'}
                                    height={'19px'}
                                />
                            </span>
                        </div>
                        <div className="sign__up__loading">
                            <div className="vector" />
                            <span className="verifying__loading__text" />
                        </div>
                        <PasswordComplexityIndicator password={password} />
                    </div>
                    <div>
                        <p className="minitext center">By signing up, I accept LucidLink’s
                            <br/>
                            <a href="https://www.lucidlink.com/terms/" target="_blank">
                                Terms of Use
                            </a> and&nbsp;
                            <a href="https://www.lucidlink.com/privacy/" target="_blank">
                                Privacy Policy
                            </a>.
                        </p>
                        <button
                            className="btn"
                            disabled={!isPasswordValid || isBusy}
                            onClick={() => handleCreateAccount(props)}
                        >
                            <span className="label__custom">Sign up</span>
                        </button>
                    </div>
                    <AuthenticationFooter message={{text: I_ALREADY_HAVE_AN_ACCOUNT_MESSAGE, label: SIGN_IN_MESSAGE}}/>
                </div>
            </div>
            <AuthenticationFooterSecondary linkHref="https://app.lucidlink.com/register" linkLabel="Try the new LucidLink" />
        </div>
    );

    function handlePasswordChange(e: React.ChangeEvent<HTMLInputElement>): void {
        const pass = e.target.value;
        const isPassValid = PasswordValidator.validatePassword(pass);
        setIsPasswordValid(isPassValid);
        setPassword(pass);
    }

    async function handleCreateAccount(passwordStepProps: PasswordStepProps): Promise<void> {
        setIsBusy(true);
        if (!passwordStepProps.email) {
            passwordStepProps.history.push(PageRoutes.Register);
            return;
        }
        try {
            const user = buildUserDataForRegistration(passwordStepProps);
            const username = await authenticationService.register(user);
            handleRegisterSuccess(username, passwordStepProps.history);
        } catch (error) {
            handleRegisterError(error);
        }
    }

    function buildUserDataForRegistration(passwordStepProps: PasswordStepProps): User {
        const queryParams = localStorage.getItem('marketingData');

        const parsedQueryParams = parseQueryParameters(queryParams as string);
        const marketingParametersData: MarketingData = {};
        parsedQueryParams.forEach(([key, value]) => {
            if (MARKETING_DATA_KEYS.includes(key as MarketingDataKey)) {
                const sanitizedValue = value.replace(/[^\w-]/g, '');
                marketingParametersData[key] = sanitizedValue;
            }
        });
        const referral = marketingParametersData?.referral;
        let utmData = buildUtmDataFromQueryParams(marketingParametersData);

        return {
            email: passwordStepProps.email,
            password: password,
            referralCode: referral,
            utmData: utmData
        };
    }

    function buildUtmDataFromQueryParams(marketingParametersData: MarketingData): string {
        const utmObject = {
            utm_source: marketingParametersData.utm_source,
            utm_campaign: marketingParametersData.utm_campaign,
            utm_id: marketingParametersData.utm_id,
            utm_term: marketingParametersData.utm_term,
            utm_medium: marketingParametersData.utm_medium
        };

        return JSON.stringify(utmObject);
    }

    function handleRegisterSuccess(username: Nullable<string>, history: any): void {
        if (!username) {
            username = '';
        }

        setIsBusy(false);
        history.push({
            pathname: PageRoutes.CodeConfirmation,
            props: {
                unconfirmedUser: username
            }
        });
    }

    function handleRegisterError(e: any) {
        let errMessage: ReactNode;
        if (e && e.message) {
            errMessage = e.message;

            if (e.name && e.name === 'UsernameExistsException') {
                errMessage = (
                    <React.Fragment>
                        {errMessage}
                    </React.Fragment>
                );
            }
            if (e.name && e.name === 'UserLambdaValidationException') {
                errMessage = 'Invalid email format. Please check your spelling or use another email.';
            } else {
                logManager.error(e);
            }
        }
        setIsBusy(false);
        setErrorMessage(errMessage as string);
    }

    async function handleEnter(event: any, emailStepProps: PasswordStepProps) {
        const ENTER_KEYCODE = 13;
        if (isPasswordValid) {
            if (event.keyCode === ENTER_KEYCODE) {
                await handleCreateAccount(emailStepProps);
            }
        }
    }

    function headerMessage(): JSX.Element {
        return (
            <>
                Welcome to LucidLink. <br /> Now, set your password.
            </>
        );
    }
}



// WEBPACK FOOTER //
// ./src/webportal/components/authentication/PasswordStep.tsx